import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Package, Plus, Pencil, Loader2 } from "lucide-react";

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", active: true });
    const [saving, setSaving] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/products");
            setProducts(r.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const openNew = () => {
        setEditing(null);
        setForm({ name: "", active: true });
        setOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({ name: p.name, active: p.active });
        setOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error("Inserisci il nome del prodotto");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/admin/products/${editing.id}`, form);
                toast.success("Prodotto aggiornato");
            } else {
                await api.post("/admin/products", form);
                toast.success("Prodotto aggiunto");
            }
            setOpen(false);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (p) => {
        if (!window.confirm(`Eliminare il prodotto "${p.name}"?\nVerrà rimosso da tutti i negozi che lo offrivano.`)) return;
        try {
            await api.delete(`/admin/products/${p.id}`);
            toast.success("Prodotto eliminato");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-products-page">
            <Toaster position="top-center" richColors />

            <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <Package className="w-5 h-5 text-magni-navy" />
                        <span className="overline">Catalogo</span>
                    </div>
                    <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy">
                        Prodotti
                    </h1>
                    <p className="text-magni-muted text-sm mt-2 max-w-2xl">
                        Definisci i prodotti che i clienti possono provare. Da{" "}
                        <span className="text-magni-navy">Negozi → Modifica</span> potrai poi selezionare quali di questi
                        ogni singolo negozio offre.
                    </p>
                </div>
                <button
                    data-testid="add-product-btn"
                    onClick={openNew}
                    className="magni-btn-primary inline-flex items-center"
                >
                    <Plus className="w-4 h-4 mr-2" /> Nuovo prodotto
                </button>
            </div>

            <div className="bg-white border border-magni-light overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-magni-muted text-sm flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Caricamento...
                    </div>
                ) : products.length === 0 ? (
                    <div className="p-10 text-center text-magni-muted text-sm">
                        Nessun prodotto ancora creato. Inizia aggiungendo "MagniStretch", "Magnigel", ecc.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-[0.12em] text-magni-muted border-b border-magni-light bg-magni-cream">
                                <th className="px-6 py-3 font-semibold">Nome</th>
                                <th className="px-6 py-3 font-semibold">Stato</th>
                                <th className="px-6 py-3 font-semibold text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p) => (
                                <tr key={p.id} className="border-b border-magni-light/60">
                                    <td className="px-6 py-3 text-magni-navy font-semibold" data-testid={`product-name-${p.id}`}>
                                        {p.name}
                                    </td>
                                    <td className="px-6 py-3">
                                        <span
                                            className="text-xs font-semibold uppercase tracking-wider px-2 py-1"
                                            style={{
                                                background: p.active ? "#e6f4ea" : "#f1f1f1",
                                                color: p.active ? "#0d6b3f" : "#5b6c78",
                                            }}
                                        >
                                            {p.active ? "Attivo" : "Disattivo"}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                        <button
                                            data-testid={`edit-product-${p.id}`}
                                            onClick={() => openEdit(p)}
                                            className="text-magni-navy hover:opacity-70 inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold"
                                        >
                                            <Pencil className="w-3 h-3" /> Modifica
                                        </button>
                                        <button
                                            data-testid={`delete-product-${p.id}`}
                                            onClick={() => handleDelete(p)}
                                            className="ml-4 text-red-600 hover:text-red-800 text-xs uppercase tracking-wider font-semibold"
                                        >
                                            Elimina
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ background: "rgba(0,0,0,0.5)" }}
                    onClick={() => !saving && setOpen(false)}
                >
                    <div
                        className="bg-white max-w-md w-full p-6 border border-magni-light"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="overline mb-3">{editing ? "Modifica" : "Nuovo"}</div>
                        <h2 className="font-serif-display text-2xl text-magni-navy mb-4">
                            {editing ? "Modifica prodotto" : "Nuovo prodotto"}
                        </h2>
                        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                            Nome *
                        </label>
                        <input
                            data-testid="product-name-input"
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Es. MagniStretch®"
                            className="magni-input mb-4"
                            autoFocus
                        />
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer mb-6">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                className="w-4 h-4 accent-[#174860]"
                            />
                            <span className="text-magni-navy">Prodotto attivo</span>
                        </label>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setOpen(false)}
                                className="magni-btn-secondary text-xs"
                                disabled={saving}
                            >
                                Annulla
                            </button>
                            <button
                                data-testid="save-product-btn"
                                onClick={handleSave}
                                disabled={saving || !form.name.trim()}
                                className="magni-btn-primary text-xs disabled:opacity-40"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-3 h-3 inline animate-spin mr-2" />
                                        Salvataggio...
                                    </>
                                ) : (
                                    "Salva"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
